import React from 'react';
import i18n from 'i18next';

import { cn } from '@/lib/utils';

export interface SizeFormat {
    width: number;
    height: number;
    depth?: number;
    unit?: string;
}

const number = (n: number) => {
    const lng = (i18n.language || 'en').slice(0, 2);
    try {
        return new Intl.NumberFormat(['en', 'fr', 'es', 'ar'].includes(lng) ? lng : 'en', { maximumFractionDigits: 1, useGrouping: false }).format(n);
    } catch {
        return String(Math.round(n * 10) / 10);
    }
};

/**
 * "60×120 cm" — a tile's or plank's face, width first, in its unit. With
 * `depth`, the thickness too ("60×120×1 cm"). `null` when there is no usable
 * size (a missing or zero side is not a size).
 */
export function formatSize(format: SizeFormat | null | undefined, options: { depth?: boolean } = {}): string | null {
    if (!format || !(format.width > 0) || !(format.height > 0)) return null;
    const sides = [format.width, format.height];
    if (options.depth && format.depth > 0) sides.push(format.depth);
    const unit = (format.unit || 'cm').trim();
    return `${sides.map(number).join('×')}${unit ? ` ${unit}` : ''}`;
}

/** A size, written the same way on every page. Says `fallback` (or "—") when there is none. */
export const SizeLabel: React.FC<{ format?: SizeFormat | null; depth?: boolean; fallback?: string; className?: string }> = ({ format, depth, fallback = '—', className }) => {
    const text = formatSize(format, { depth });
    return (
        <span className={cn('tabular-nums', !text && 'text-muted-foreground', className)} dir="ltr">
            {text ?? fallback}
        </span>
    );
};
